import { omitFalsy } from "@frend-digital/utils";
import { defu } from "defu";
import type { Metadata } from "next";
import type { ISbStoryData } from "storyblok-js-client";

type BlokMetadata = {
  _uid: string;
  title: string;
  plugin: string;
  og_image: string;
  og_title: string;
  description: string;
  twitter_image: string;
  twitter_title: string;
  og_description: string;
  twitter_description: string;
};

type StoryMetadata = {
  metadata: Partial<BlokMetadata>;
};

export const hasMetadata = <T extends ISbStoryData>(
  story: T
): story is T & ISbStoryData<StoryMetadata> => {
  const metadata = (story as unknown as ISbStoryData<StoryMetadata>)?.content
    ?.metadata;
  return metadata && Object.keys(metadata).length > 0 && !!metadata.title;
};

interface MetadataConfig {
  /**
   * The site URL
   * @example "https://example.com"
   */
  site: string;
  /**
   * The site name
   * @example "Example"
   */
  siteName: string;
  /**
   * A function to get the title of the page - only used when no storyblok metadata is present.
   * @param title The title of the page
   * @returns The title of the page
   */
  getTitle?: (title: string) => string;
}
export function createMetadata({ site, siteName, getTitle }: MetadataConfig) {
  const generateStoryMetadata = async (
    story: ISbStoryData | null | undefined,
    additionalMetadata: Partial<BlokMetadata> = {}
  ): Promise<Metadata> => {
    if (!story) return additionalMetadata;

    const defaultTitle = getTitle
      ? getTitle(story.name)
      : siteName
        ? `${story.name} | ${siteName}`
        : story.name;

    if (!hasMetadata(story)) {
      // Todo - Notify content editor of missing metadata
      return defu(additionalMetadata, {
        title: defaultTitle,
      });
    }

    const metadata = omitFalsy(story.content.metadata);

    const title = metadata.title ?? defaultTitle;

    return defu(
      {
        metadataBase: new URL(site),
        title,
        description: metadata.description,
        keywords: story.tag_list.length > 0 ? story.tag_list : undefined,
        openGraph: {
          title: metadata.og_title ?? title,
          description: metadata.og_description ?? metadata.description,
          siteName: siteName,
          images: metadata.og_image ? [{ url: metadata.og_image }] : undefined,
        },
      },
      additionalMetadata
    );
  };

  return generateStoryMetadata;
}
